import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { AGENT_STATUSES, AGENT_TYPES } from "@/features/shared/api/enums";

import { AgentRowActions, type AgentRow } from "./agent-row-actions";

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Colonnes de la liste des agents (`POST /Agents/all`). Filtres select sur
 * les énumérations de l'annexe A, filtre texte sur le code agence ; données
 * métier en `NoTranslate` (règle notranslate).
 */
export const agentsColumns: ColumnsClarFactory<AgentRow> = () => [
  {
    id: "fullName",
    accessorKey: "fullName",
    header: t("kyc.agents.columns.fullName"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    // Pas dans AgentFilter (agencyCode / agentType / status).
    meta: { type: "denied" },
  },
  {
    id: "keycloakUserId",
    accessorKey: "keycloakUserId",
    header: t("kyc.agents.columns.keycloakUserId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "agencyCode",
    accessorKey: "agencyCode",
    header: t("kyc.agents.columns.agencyCode"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "zone",
    accessorKey: "zone",
    header: t("kyc.agents.columns.zone"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "agentType",
    accessorKey: "agentType",
    header: t("kyc.agents.columns.agentType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(AGENT_TYPES) },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("kyc.agents.columns.status"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(AGENT_STATUSES) },
  },
  {
    id: "floatWalletId",
    accessorKey: "floatWalletId",
    header: t("kyc.agents.columns.floatWalletId"),
    cell: ({ getValue }) => {
      const value = (getValue() as string | undefined) ?? "";
      return (
        <NoTranslate as="span" title={value}>
          {value ? `${value.slice(0, 8)}…` : "—"}
        </NoTranslate>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) => <AgentRowActions agent={row.original} />,
    meta: { type: "denied" },
  },
];
